import { useCallback } from 'react';
import { ScreenField, ScreenPicker, ScreenPickerProps } from 'app/area/codetable/item/screen-renderer/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { CodeItem, SafetyLimit } from 'app/area/codetable/item/types-store';
import { ReadOnlyType, UpdateSafetyLimitPayload } from 'app/area/codetable/item/types';
import { ValidationProps } from 'ui';
import { createSafetyLimitId } from 'app/area/codetable/item/codetable-item-helpers';

export interface SafetyLimitTileProps extends ScreenPickerProps {
  safetyLimit: SafetyLimit;
  removeSafetyLimit: (codeItem: CodeItem, toRemove: SafetyLimit,
    validation: { [key: string]: ValidationProps }) => void;
  updateSafetyLimit: (payload: UpdateSafetyLimitPayload) => void;
}

export function useSafetyLimitTile(props: SafetyLimitTileProps) {
  const { codeItem, safetyLimit, validation, removeSafetyLimit, updateSafetyLimit, readOnlyConfig } = props;

  const updateMinValue = useCallback(
    (value: number) => updateSafetyLimit({ value, key: ScreenPicker.minValue, codeItem, validation, safetyLimit }),
    [codeItem, validation, safetyLimit, updateSafetyLimit]
  );

  const updateMaxValue = useCallback(
    (value: number) => updateSafetyLimit({ value, key: ScreenPicker.maxValue, codeItem, validation, safetyLimit }),
    [codeItem, validation, safetyLimit, updateSafetyLimit]
  );

  const updateWarningDeviationPercent = useCallback(
    (value: number) => updateSafetyLimit({ value, key: ScreenField.warningDeviationPercent, codeItem, validation, safetyLimit }),
    [codeItem, validation, safetyLimit, updateSafetyLimit]
  );

  const remove = useCallback(
    () => removeSafetyLimit(codeItem, safetyLimit, validation),
    [codeItem, safetyLimit, validation, removeSafetyLimit]
  );

  const readOnly = readOnlyConfig?.[ReadOnlyType.all] ?? false;

  const { minValue, maxValue, warningDeviationPercent, application } = safetyLimit ?? {};

  const minRequired = !isDefinedIncludingZero(maxValue);
  const maxRequired = !isDefinedIncludingZero(minValue);

  const validationId = createSafetyLimitId(ScreenPicker.application, application?.value);
  const errors = validation?.[validationId]?.messages ?? [];
  const hasErrors = errors.length > 0;

  return {
    updateMinValue,
    updateMaxValue,
    updateWarningDeviationPercent,
    remove,
    readOnly,
    minValue,
    maxValue,
    warningDeviationPercent,
    application,
    minRequired,
    maxRequired,
    errors,
    hasErrors
  };
}
